import { useState } from "react";
import { api, COMPANY, SERVICES } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { CheckCircle2, Phone, MessageSquare, CalendarClock, Upload, X } from "lucide-react";
import { onCallClick, onTextClick } from "@/lib/dialer";

export default function SchedulePage() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name:"", phone:"", email:"", address:"", city:"", service_type: SERVICES[0].title, preferred_date:"", preferred_time:"Morning (8am–12pm)", notes:"", photos:[] });
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k,v) => setForm(f=>({...f, [k]:v}));

  const onFiles = async (files) => {
    const arr = await Promise.all(Array.from(files).slice(0,5).map(f => new Promise(res => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
    })));
    setForm(f => ({ ...f, photos: [...f.photos, ...arr].slice(0,5) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/schedule", { ...form, language: lang });
      setSubmitted(r.data);
      toast.success("Request received");
      window.scrollTo({top:0, behavior:"smooth"});
    } catch (err) {
      toast.error("Could not submit. Please call us directly.");
    } finally { setLoading(false); }
  };

  return (
    <main className="section-pad" data-testid="schedule-page">
      <div className="mx-auto max-w-5xl px-5 md:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.22em] text-ice-400">Book Service</div>
        <h1 className="mt-3 font-display text-4xl md:text-5xl font-black">Schedule Service</h1>
        <p className="mt-3 text-silver-300 max-w-2xl">Choose service type, pick a time, and our team will confirm by text or call. For emergency service, call directly.</p>

        {submitted ? (
          <div className="mt-10 rounded-3xl bg-ice-400/10 ring-1 ring-ice-400/30 p-8 text-center" data-testid="schedule-success">
            <CheckCircle2 className="size-10 text-ice-400 mx-auto"/>
            <h2 className="mt-3 font-display text-2xl font-black">You&apos;re scheduled!</h2>
            <p className="mt-2 text-silver-300">{submitted.message}</p>
            <p className="mt-1 text-xs text-silver-400">Reference: {submitted.id}</p>
            <div className="mt-5 flex justify-center gap-3">
              <a href={`tel:${COMPANY.phoneTel}`} onClick={onCallClick} className="btn-primary rounded-full px-4 py-2.5 text-sm flex items-center gap-2"><Phone className="size-4"/> Call Us</a>
              <a href={COMPANY.sms} onClick={onTextClick} className="btn-ghost rounded-full px-4 py-2.5 text-sm flex items-center gap-2"><MessageSquare className="size-4"/> Text Us</a>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 rounded-3xl bg-ink-800 ring-1 ring-white/10 p-6 md:p-8 grid gap-5" data-testid="schedule-form">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Full Name *"><input required value={form.name} onChange={e=>set("name",e.target.value)} className="input" data-testid="sch-name"/></Field>
              <Field label="Phone *"><input required value={form.phone} onChange={e=>set("phone",e.target.value)} className="input" data-testid="sch-phone"/></Field>
              <Field label="Email"><input type="email" value={form.email} onChange={e=>set("email",e.target.value)} className="input" data-testid="sch-email"/></Field>
              <Field label="City"><input value={form.city} onChange={e=>set("city",e.target.value)} className="input" data-testid="sch-city"/></Field>
              <Field label="Street Address" className="md:col-span-2"><input value={form.address} onChange={e=>set("address",e.target.value)} className="input" data-testid="sch-address"/></Field>
              <Field label="Service Type">
                <select value={form.service_type} onChange={e=>set("service_type",e.target.value)} className="input" data-testid="sch-service">
                  {SERVICES.map(s => <option key={s.key}>{s.title}</option>)}
                </select>
              </Field>
              <Field label="Preferred Date"><input type="date" value={form.preferred_date} onChange={e=>set("preferred_date",e.target.value)} className="input" data-testid="sch-date"/></Field>
              <Field label="Preferred Time">
                <select value={form.preferred_time} onChange={e=>set("preferred_time",e.target.value)} className="input" data-testid="sch-time">
                  {["Morning (8am–12pm)","Afternoon (12pm–4pm)","Evening (4pm–7pm)","Anytime"].map(o=> <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Notes" className="md:col-span-2">
                <textarea value={form.notes} onChange={e=>set("notes",e.target.value)} className="input min-h-[100px]" placeholder="Equipment age, symptoms, special access info..." data-testid="sch-notes"/>
              </Field>
              <Field label="Upload Photos (equipment / issue — up to 5)" className="md:col-span-2">
                <label className="flex items-center gap-2 text-xs rounded-xl bg-ink-700 ring-1 ring-white/10 px-3 py-3 cursor-pointer hover:bg-ink-600 w-fit" data-testid="sch-upload-label">
                  <Upload className="size-4"/> Add photos
                  <input type="file" multiple accept="image/*" onChange={e=>onFiles(e.target.files)} className="hidden" data-testid="sch-upload"/>
                </label>
                {form.photos.length>0 && (
                  <div className="mt-3 flex gap-2 flex-wrap" data-testid="sch-photos">
                    {form.photos.map((p,i)=>(
                      <div key={p.slice(-32) + i} className="relative">
                        <img src={p} alt="" className="size-16 rounded-lg object-cover ring-1 ring-white/10"/>
                        <button type="button" onClick={()=>setForm(f=>({...f, photos: f.photos.filter((_,k)=>k!==i)}))} className="absolute -top-1.5 -right-1.5 size-5 rounded-full bg-red-500 grid place-items-center"><X className="size-3"/></button>
                      </div>
                    ))}
                  </div>
                )}
              </Field>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <p className="text-xs text-silver-400">By submitting, you agree to be contacted about your request.</p>
              <button type="submit" disabled={loading} className="btn-primary rounded-full px-6 py-3 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="sch-submit">
                <CalendarClock className="size-4"/> {loading ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </form>
        )}
      </div>
      <style>{`.input { background:#1C274C; border-radius: 0.75rem; padding: 0.75rem 1rem; font-size: 0.875rem; outline: none; width: 100%; border: 1px solid rgba(255,255,255,0.08); color: white; }
      .input:focus { border-color: #00A8E8; box-shadow: 0 0 0 3px rgba(0,168,232,0.18); }`}</style>
    </main>
  );
}

function Field({ label, children, className="" }) {
  return (
    <label className={`grid gap-1.5 ${className}`}>
      <span className="text-xs uppercase tracking-[0.18em] text-silver-400">{label}</span>
      {children}
    </label>
  );
}
